import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import {
  createManualTag,
  deleteManualTag,
  getManualTags,
  updateManualTag,
} from 'API/contact-management';
import PaiButton from 'features/ui/base-components/pai-button';
import { useUserData } from 'hooks/use-user-data';
import useErrorStore from 'stores/error-store';
import { ManualTag } from '../contact-management.types';

import CreateTagDialog from './create-tag-dialog';
import DeleteTagDialog from './delete-tag-dialog';
import EditTagDialog from './edit-tag-dialog';
import TagTable from './tag-table';

interface DialogState {
  open: boolean;
  loading: boolean;
  tagName?: string; // used for create/edit
  tag?: ManualTag | null;
}

const TagManagement: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [tags, setTags] = useState<ManualTag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Create
  const [createTagDialog, setCreateTagDialog] = useState<DialogState>({
    open: false,
    loading: false,
    tagName: '',
  });

  // Edit
  const [editTagDialog, setEditTagDialog] = useState<DialogState>({
    open: false,
    loading: false,
    tagName: '',
    tag: null,
  });

  // Delete
  const [deleteTagDialog, setDeleteTagDialog] = useState<DialogState>({
    open: false,
    loading: false,
    tag: null,
  });

  /**
   * Fetch tags from server
   */
  const fetchTags = async (newPage: number = page, newRowsPerPage: number = rowsPerPage) => {
    setTagsLoading(true);

    const res = await getManualTags(newPage, newRowsPerPage);
    if (!res.error) {
      setTags(res.tags || []);
      setTotal(res.total || 0);
    } else {
      setTags([]);
      setTotal(0);
      handleOpenErrorDialog(res.error, '/contact-management/manual-tags', 'get');
    }
    setTagsLoading(false);
  };

  useEffect(() => {
    fetchTags();
  }, []);

  /**
   * Pagination handlers
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchTags(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchTags(0, newRowsPerPage);
  };

  /**
   * Create tag dialog
   */
  const handleOpenCreateTagDialog = () => {
    setCreateTagDialog({ open: true, loading: false, tagName: '' });
  };
  const handleCloseCreateTagDialog = () => {
    setCreateTagDialog({ open: false, loading: false, tagName: '' });
  };
  const handleCreateTagNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateTagDialog((prev) => ({ ...prev, tagName: event.target.value }));
  };
  const handleCreateTag = async () => {
    setCreateTagDialog((prev) => ({ ...prev, loading: true }));

    const { tagName = '' } = createTagDialog;
    const res = await createManualTag(tagName);
    if (!res.error) {
      fetchTags();
      handleCloseCreateTagDialog();
    } else {
      setCreateTagDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, '/contact-management/manual-tags', 'post');
    }
  };

  /**
   * Edit tag dialog
   */
  const handleOpenEditTagDialog = (tag: ManualTag) => {
    return () => {
      setEditTagDialog({ open: true, loading: false, tagName: tag.name, tag });
    };
  };
  const handleCloseEditTagDialog = () => {
    setEditTagDialog({ open: false, loading: false, tagName: '', tag: null });
  };
  const handleEditTagNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditTagDialog((prev) => ({ ...prev, tagName: event.target.value }));
  };
  const handleEditTag = async () => {
    const { tagName = '', tag } = editTagDialog;
    if (_.isNil(tag)) return;

    setEditTagDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateManualTag(tag._id, tagName);
    if (!res.error) {
      setTags((prev) =>
        prev.map((existing) =>
          existing._id === tag._id ? { ...existing, name: tagName } : existing
        )
      );
      handleCloseEditTagDialog();
    } else {
      setEditTagDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, '/contact-management/manual-tags/:tagId', 'put');
    }
  };

  /**
   * Delete tag dialog
   */
  const handleOpenDeleteTagDialog = (tag: ManualTag) => {
    return () => {
      setDeleteTagDialog({ open: true, loading: false, tag });
    };
  };
  const handleCloseDeleteTagDialog = () => {
    setDeleteTagDialog({ open: false, loading: false, tag: null });
  };
  const handleDeleteTag = async () => {
    const { tag } = deleteTagDialog;
    if (_.isNil(tag)) return;

    setDeleteTagDialog((prev) => ({ ...prev, loading: true }));
    const res = await deleteManualTag(tag._id);

    if (!res.error) {
      // If we deleted the last item on the page, refetch the previous page
      if (tags.length === 1 && page !== 0) {
        fetchTags(page - 1);
        setPage(page - 1);
      } else {
        setTags((prev) => prev.filter((existing) => existing._id !== tag._id));
      }
      handleCloseDeleteTagDialog();
    } else {
      setDeleteTagDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, '/contact-management/manual-tags/:tagId', 'delete');
    }
  };

  return (
    <div className={classes.contactList}>
      {/* Header */}
      <div className={classes.listHeader}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <PaiButton text={`+ ${t('manualTag')}`} action={handleOpenCreateTagDialog} />
        </Stack>
      </div>

      {/* Table Content */}
      <div className={classes.listContent}>
        <TagTable
          tags={tags}
          tagsLoading={tagsLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleOpenEditTagDialog={handleOpenEditTagDialog}
          handleOpenDeleteTagDialog={handleOpenDeleteTagDialog}
        />
      </div>

      {/* Create Tag Dialog */}
      <CreateTagDialog
        open={createTagDialog.open}
        loading={createTagDialog.loading}
        tagName={createTagDialog.tagName || ''}
        handleTagNameChange={handleCreateTagNameChange}
        handleClose={handleCloseCreateTagDialog}
        handleCreateTag={handleCreateTag}
      />

      {/* Edit Tag Dialog */}
      <EditTagDialog
        open={editTagDialog.open}
        loading={editTagDialog.loading}
        tagName={editTagDialog.tagName || ''}
        handleTagNameChange={handleEditTagNameChange}
        handleClose={handleCloseEditTagDialog}
        handleEditTag={handleEditTag}
      />

      {/* Delete Tag Dialog */}
      <DeleteTagDialog
        open={deleteTagDialog.open}
        loading={deleteTagDialog.loading}
        tag={deleteTagDialog.tag ?? null}
        handleClose={handleCloseDeleteTagDialog}
        handleDeleteTag={handleDeleteTag}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  contactList: {
    height: '100%',
    width: '100%',
    borderRadius: '12px',
    background: '#FFF',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  listHeader: {
    padding: '8px 32px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  listContent: {
    position: 'relative',
    height: '100%',
    padding: '8px',
    overflow: 'auto',
  },
}));

export default TagManagement;
